import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';

const SETTINGS_PIN = '4444';

const UserManagement = () => {
  const { token } = useAuth();
  const { language, isRTL } = useLanguage();

  // PIN protection
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  // Users state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Edit user state
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', password: '', role: '' });
  const [userMessage, setUserMessage] = useState({ type: '', text: '' });

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    if (pinVerified) {
      fetchUsers();
    }
  }, [pinVerified]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === SETTINGS_PIN) {
      setPinVerified(true);
      setPinError('');
    } else {
      setPinError(language === 'ar' ? 'رمز PIN غير صحيح' : 'Incorrect PIN');
      setPin('');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setUserMessage({ type: '', text: '' });
  };

  const closeEditUser = () => {
    setEditingUser(null);
    setEditUserData({ name: '', email: '', password: '', role: '' });
    setUserMessage({ type: '', text: '' });
  };

  const saveUser = async () => {
    if (!editingUser) return;

    try {
      const updateData = {};
      if (editUserData.name !== editingUser.name) updateData.name = editUserData.name;
      if (editUserData.email !== editingUser.email) updateData.email = editUserData.email;
      if (editUserData.password) updateData.password = editUserData.password;
      if (editUserData.role !== editingUser.role) updateData.role = editUserData.role;

      if (Object.keys(updateData).length === 0) {
        setUserMessage({ type: 'info', text: language === 'ar' ? 'لا توجد تغييرات' : 'No changes made' });
        return;
      }

      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
        setUserMessage({ type: 'success', text: language === 'ar' ? 'تم تحديث المستخدم' : 'User updated successfully' });
        setTimeout(() => closeEditUser(), 1500);
      } else {
        const error = await response.json();
        setUserMessage({ type: 'error', text: error.error || (language === 'ar' ? 'فشل التحديث' : 'Update failed') });
      }
    } catch (error) {
      setUserMessage({ type: 'error', text: language === 'ar' ? 'فشل التحديث' : 'Update failed' });
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المستخدم؟' : 'Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setUsers(users.filter(u => u.id !== userId));
        setMessage({ type: 'success', text: language === 'ar' ? 'تم حذف المستخدم' : 'User deleted' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || (language === 'ar' ? 'فشل الحذف' : 'Delete failed') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: language === 'ar' ? 'فشل الحذف' : 'Delete failed' });
    }
  };

  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // PIN Entry Screen
  if (!pinVerified) {
    return (
      <div className="pin-overlay">
        <div className="pin-modal">
          <h2>{language === 'ar' ? '🔒 إدخال رمز PIN' : '🔒 Enter PIN'}</h2>
          <p>{language === 'ar' ? 'أدخل رمز PIN للوصول إلى إدارة المستخدمين' : 'Enter PIN to access user management'}</p>

          <form onSubmit={handlePinSubmit}>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pin}
              onChange={(e) => { setPin(e.target.value); setPinError(''); }}
              className="form-input"
              style={{
                textAlign: 'center',
                fontSize: '24px',
                letterSpacing: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}
              placeholder="••••"
              autoFocus
            />

            {pinError && <div style={{ color: '#ef4444', marginBottom: '16px' }}>{pinError}</div>}

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
              {language === 'ar' ? 'دخول' : 'Enter'}
            </button>
          </form>

          <Link to="/dashboard4/settings" style={{ display: 'block', marginTop: '20px', color: 'var(--gray-500)', fontSize: '14px' }}>
            {language === 'ar' ? '← العودة للإعدادات' : '← Back to Settings'}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
      <div className="container">
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</Link>
            <Link to="/dashboard4/products">{language === 'ar' ? 'المنتجات' : 'Products'}</Link>
            <Link to="/dashboard4/orders">{language === 'ar' ? 'الطلبات' : 'Orders'}</Link>
            <Link to="/dashboard4/create-order">{language === 'ar' ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{language === 'ar' ? 'المخزون' : 'Inventory'}</Link>
            <Link to="/dashboard4/stats">{language === 'ar' ? 'الإحصائيات' : 'Stats'}</Link>
            <Link to="/dashboard4/settings">{language === 'ar' ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
            {message.text}
          </div>
        )}

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary)' }}>{users.length}</div>
            <div style={{ color: 'var(--gray-600)', fontSize: '14px' }}>{language === 'ar' ? 'إجمالي المستخدمين' : 'Total Users'}</div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1d4ed8' }}>{users.filter(u => u.role === 'admin').length}</div>
            <div style={{ color: 'var(--gray-600)', fontSize: '14px' }}>{language === 'ar' ? 'المديرين' : 'Admins'}</div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#6b7280' }}>{users.filter(u => u.role === 'user').length}</div>
            <div style={{ color: 'var(--gray-600)', fontSize: '14px' }}>{language === 'ar' ? 'المستخدمين' : 'Users'}</div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="admin-card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', padding: '16px' }}>
            <input
              type="text"
              className="form-input"
              placeholder={language === 'ar' ? 'بحث بالاسم أو البريد...' : 'Search by name or email...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: '1', minWidth: '200px' }}
            />
            <select
              className="form-input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ width: 'auto', minWidth: '150px' }}
            >
              <option value="all">{language === 'ar' ? 'جميع الأدوار' : 'All Roles'}</option>
              <option value="admin">{language === 'ar' ? 'مدير' : 'Admin'}</option>
              <option value="user">{language === 'ar' ? 'مستخدم' : 'User'}</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="admin-card">
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', textAlign: isRTL ? 'right' : 'left' }}>
                  <th style={{ padding: '14px 16px', fontWeight: '600', borderBottom: '1px solid var(--gray-200)' }}>
                    {language === 'ar' ? 'الاسم' : 'Name'}
                  </th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', borderBottom: '1px solid var(--gray-200)' }}>
                    {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                  </th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', borderBottom: '1px solid var(--gray-200)' }}>
                    {language === 'ar' ? 'الدور' : 'Role'}
                  </th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', borderBottom: '1px solid var(--gray-200)' }}>
                    {language === 'ar' ? 'تاريخ التسجيل' : 'Registered'}
                  </th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', borderBottom: '1px solid var(--gray-200)', textAlign: 'center' }}>
                    {language === 'ar' ? 'الإجراءات' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '14px 16px', fontWeight: '500' }}>{user.name}</td>
                    <td style={{ padding: '14px 16px', color: 'var(--gray-600)' }}>{user.email}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: user.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                        color: user.role === 'admin' ? '#1d4ed8' : '#6b7280'
                      }}>
                        {user.role === 'admin' ? (language === 'ar' ? 'مدير' : 'Admin') : (language === 'ar' ? 'مستخدم' : 'User')}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--gray-500)', fontSize: '13px' }}>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-AE') : '-'}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => openEditUser(user)}
                          className="btn btn-sm"
                          style={{
                            padding: '6px 14px',
                            fontSize: '13px',
                            background: 'var(--primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px'
                          }}
                        >
                          {language === 'ar' ? 'تعديل' : 'Edit'}
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="btn btn-sm"
                          style={{
                            padding: '6px 14px',
                            fontSize: '13px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '6px'
                          }}
                        >
                          {language === 'ar' ? 'حذف' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--gray-500)' }}>
                      {searchTerm || roleFilter !== 'all'
                        ? (language === 'ar' ? 'لا توجد نتائج للبحث' : 'No results found')
                        : (language === 'ar' ? 'لا يوجد مستخدمون' : 'No users found')
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Results count */}
          {filteredUsers.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--gray-200)', color: 'var(--gray-500)', fontSize: '13px' }}>
              {language === 'ar'
                ? `عرض ${filteredUsers.length} من ${users.length} مستخدم`
                : `Showing ${filteredUsers.length} of ${users.length} users`
              }
            </div>
          )}
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h3 style={{ marginBottom: '20px', fontSize: '18px' }}>
                {language === 'ar' ? 'تعديل المستخدم' : 'Edit User'}
              </h3>

              {userMessage.text && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  background: userMessage.type === 'success' ? '#d1fae5' : userMessage.type === 'error' ? '#fee2e2' : '#e0f2fe',
                  color: userMessage.type === 'success' ? '#065f46' : userMessage.type === 'error' ? '#dc2626' : '#0369a1'
                }}>
                  {userMessage.text}
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{language === 'ar' ? 'الاسم' : 'Name'}</label>
                <input
                  type="text"
                  className="form-input"
                  value={editUserData.name}
                  onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
                <input
                  type="email"
                  className="form-input"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">
                  {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                  <span style={{ color: 'var(--gray-500)', fontSize: '12px', marginRight: isRTL ? 0 : '8px', marginLeft: isRTL ? '8px' : 0 }}>
                    ({language === 'ar' ? 'اتركه فارغاً للإبقاء على كلمة المرور الحالية' : 'leave empty to keep current'})
                  </span>
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={editUserData.password}
                  onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })}
                  placeholder={language === 'ar' ? 'كلمة مرور جديدة...' : 'New password...'}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label className="form-label">{language === 'ar' ? 'الدور' : 'Role'}</label>
                <select
                  className="form-input"
                  value={editUserData.role}
                  onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                  style={{ cursor: 'pointer' }}
                >
                  <option value="user">{language === 'ar' ? 'مستخدم' : 'User'}</option>
                  <option value="admin">{language === 'ar' ? 'مدير' : 'Admin'}</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn"
                  onClick={closeEditUser}
                  style={{
                    flex: 1,
                    background: 'var(--gray-100)',
                    color: 'var(--gray-700)',
                    border: 'none'
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveUser}
                  style={{ flex: 1 }}
                >
                  {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
